import type { PlanPersistence } from "../design/planSession";
import { invalidateHttpReads } from "./httpExecutor";
import { reviveWire } from "./wireCollections";
import type { PlanModel } from "../geometry";

/**
 * TALKING TO SOMEBODY ELSE'S PROJECT (spec §65C).
 *
 * Everywhere else in Berrier Reno the browser reaches data by sending SQL to
 * its own workspace. It cannot do that here, and deliberately never will: a
 * shared project lives in the OWNER's database, and giving this browser SQL
 * against that file would give it everything the owner has.
 *
 * So this client speaks in domain operations, and the server decides — per
 * operation, from the share table — whether the signed-in person may. Every
 * call carries a project id and nothing that could name a workspace.
 */

export interface SharedProjectCard {
  projectId: string;
  name: string;
  jobNumber: string | null;
  street: string;
  city: string;
  state: string;
  ownerName: string;
  role: string;
  sharedAt: string;
}

export interface SharedProjectContext {
  projectId: string;
  name: string;
  jobNumber: string | null;
  projectType: string;
  address: { street: string; city: string; state: string; zip: string };
  owner: { id: string; name: string };
  role: "owner" | "design-collaborator";
  floors: Array<{ id: string; name: string; floorType: string; sortOrder: number }>;
  rooms: Array<{ id: string; floorId: string; name: string; roomTypeId: string | null }>;
}

export interface SharedDesign {
  id: string;
  name: string;
  kind: string;
  revision: number;
  authorAccountId: string | null;
  authorName: string;
  canEdit: boolean;
  updatedAt: string;
}

export interface SharePerson {
  recipientId: string;
  name: string;
  email: string;
  role: string;
  createdAt: string;
}

export class SharedAccessError extends Error {}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, { credentials: "same-origin", ...init });
  // An environment with no sharing server (vite dev preview, desktop)
  // answers with the SPA's own HTML at 200 — that is "not available here",
  // never a payload. Trusting it produced an undefined .projects and a
  // crashed Projects screen.
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("json")) {
    throw new SharedAccessError("Sharing is not available here.");
  }
  const payload = (await response.json().catch(() => ({}))) as { error?: string } & T;
  if (!response.ok) {
    throw new SharedAccessError(payload.error ?? "That is no longer available to you.");
  }
  return payload;
}

const post = <T>(path: string, body: unknown): Promise<T> =>
  call<T>(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

/* ------------------------------------------------------------------ *
 * Owning and sharing
 * ------------------------------------------------------------------ */

export async function listSharedWithMe(): Promise<SharedProjectCard[]> {
  const result = await call<{ projects: SharedProjectCard[] }>("/api/shared/projects");
  return result.projects ?? [];
}

export async function listPeopleWithAccess(projectId: string): Promise<SharePerson[]> {
  const result = await call<{ people: SharePerson[] }>(
    `/api/projects/shares?projectId=${encodeURIComponent(projectId)}`,
  );
  return result.people;
}

export async function shareProject(projectId: string, email: string): Promise<void> {
  await post("/api/projects/share", { projectId, email });
}

export async function unshareProject(projectId: string, recipientId: string): Promise<void> {
  await post("/api/projects/unshare", { projectId, recipientId });
}

/* ------------------------------------------------------------------ *
 * Reading a shared project
 * ------------------------------------------------------------------ */

export const loadSharedContext = (projectId: string): Promise<SharedProjectContext> =>
  call<SharedProjectContext>(`/api/shared/context?projectId=${encodeURIComponent(projectId)}`);

export async function listSharedDesigns(projectId: string): Promise<SharedDesign[]> {
  const result = await call<{ designs: SharedDesign[] }>(
    `/api/shared/designs?projectId=${encodeURIComponent(projectId)}`,
  );
  return result.designs;
}

export async function createSharedDesign(
  projectId: string,
  name: string,
  fromDesignId: string | null = null,
): Promise<SharedDesign> {
  // The source rides along (spec §72): choosing "Josh — Plan A" must
  // branch from Josh’s plan, not silently from Existing.
  const result = await post<{ design: SharedDesign }>("/api/shared/designs", { projectId, name, fromDesignId });
  return result.design;
}

export async function loadSharedFloor(
  projectId: string,
  designId: string,
  floorId: string,
): Promise<{ model: PlanModel; revision: number }> {
  const payload = await call<{ model: unknown; revision: number }>(
    `/api/shared/floor?projectId=${encodeURIComponent(projectId)}` +
      `&designId=${encodeURIComponent(designId)}&floorId=${encodeURIComponent(floorId)}`,
  );
  return { model: reviveWire(payload.model) as PlanModel, revision: payload.revision };
}

/* ------------------------------------------------------------------ *
 * Editing a shared design
 * ------------------------------------------------------------------ */

/**
 * The collaborator's half of the ONE autosave path (spec §65I). PlanSession
 * batches, debounces and — crucially — keeps a failed batch pending exactly
 * as it does for the owner. All that differs is who runs the write.
 */
export function sharedPlanPersistence(projectId: string, onRevision?: (revision: number) => void): PlanPersistence {
  return {
    async loadFloor(designId, floorId) {
      const result = await loadSharedFloor(projectId, designId, floorId);
      onRevision?.(result.revision);
      return result.model;
    },
    async applyBatch(designId, floorId, batch) {
      // One request per committed batch, each atomic on the server. A
      // rejected save throws, and PlanSession keeps the work pending.
      for (const item of batch) {
        const result = await post<{ revision: number }>("/api/shared/apply", {
          projectId,
          designId,
          floorId,
          changes: item.changes,
          direction: item.direction,
        });
        onRevision?.(result.revision);
      }
    },
  };
}

export const acquireEditorLease = (
  projectId: string,
  designId: string,
): Promise<{ held: boolean; heldBy: { accountId: string; name: string } | null }> =>
  post("/api/shared/lease", { projectId, designId, action: "acquire" }).catch((err) => {
    if (err instanceof SharedAccessError) return { held: false, heldBy: null };
    throw err;
  }) as Promise<{ held: boolean; heldBy: { accountId: string; name: string } | null }>;

export const releaseEditorLease = (projectId: string, designId: string): Promise<unknown> =>
  post("/api/shared/lease", { projectId, designId, action: "release" }).catch(() => undefined);

/**
 * The owner edits through their own repositories, so the server never sees
 * the write. This tells it one landed, AFTER it committed, so live viewers
 * are told about durable state and nothing else (spec §65I).
 */
export const announceOwnerEdit = (projectId: string, designId: string): void => {
  void post("/api/shared/touch", { projectId, designId }).catch(() => undefined);
};

/* ------------------------------------------------------------------ *
 * Presence
 * ------------------------------------------------------------------ */

/**
 * CURSOR TRAFFIC (spec §65L).
 *
 * Coalesced to one small request every `intervalMs` WHILE MOVING, carrying
 * only the latest position — and none at all once the pointer stops. This is
 * never persisted anywhere: it reaches memory on the server and a frame on
 * the wire.
 */
export function createPresenceSender(projectId: string, designId: string, intervalMs = 80) {
  let pending: { x: number; y: number } | null = null;
  let pendingSelection: string | null = null;
  let dirty = false;
  let timer: ReturnType<typeof setInterval> | null = null;

  const flush = () => {
    if (!dirty) {
      // Nothing moved since the last tick: stop sending entirely rather than
      // heartbeating a still cursor.
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      return;
    }
    dirty = false;
    void post("/api/shared/presence", { projectId, designId, cursor: pending, selection: pendingSelection }).catch(
      () => undefined,
    );
  };

  return {
    move(point: { x: number; y: number } | null, selection: string | null = null) {
      pending = point;
      pendingSelection = selection;
      dirty = true;
      if (!timer) {
        flush();
        timer = setInterval(flush, intervalMs);
      }
    },
    stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      pending = null;
      dirty = false;
    },
  };
}

/* ------------------------------------------------------------------ *
 * The live channel
 * ------------------------------------------------------------------ */

export type LiveMessage =
  | { type: "design-changed"; designId: string; revision: number }
  | { type: "design-created"; designId: string }
  | { type: "presence"; designId: string; people: LivePerson[] }
  | { type: "lease"; designId: string; heldBy: { accountId: string; name: string } | null }
  | { type: "access-revoked"; projectId: string };

export interface LivePerson {
  accountId: string;
  name: string;
  color: string;
  cursor: { x: number; y: number } | null;
  selection: string | null;
  editing: boolean;
}

/**
 * Server-Sent Events: plain HTTP, the ordinary session cookie, the existing
 * strict CSP, and a browser that reconnects by itself. The caller resyncs
 * authoritative state on open and on every reconnect, so a missed frame
 * costs one fetch rather than a silently wrong drawing (spec §65J).
 */
export function openLiveChannel(options: {
  projectId: string;
  designId: string | null;
  onMessage: (message: LiveMessage) => void;
  onReconnect?: () => void;
}): () => void {
  const query = new URLSearchParams({ projectId: options.projectId });
  if (options.designId) query.set("designId", options.designId);
  const source = new EventSource(`/api/shared/live?${query.toString()}`);
  let everOpened = false;

  source.onopen = () => {
    // The first open is the caller's own initial load; every one after it is
    // a reconnection, and whatever happened meanwhile was missed.
    if (everOpened) {
      invalidateHttpReads();
      options.onReconnect?.();
    }
    everOpened = true;
  };
  source.onmessage = (event) => {
    // Somebody else changed the project: no shared read may answer for it.
    invalidateHttpReads();
    try {
      options.onMessage(JSON.parse(event.data) as LiveMessage);
    } catch {
      /* a frame we do not understand is not worth breaking the page for */
    }
  };

  return () => source.close();
}
